import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    Briefcase, Users, Radar, GitMerge, MessageSquare, Coins, ShieldCheck, Megaphone, Sparkles,
    TrendingUp, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { openChat } from "@/lib/chatBus";
import SetupChecklistWidget from "@/components/dashboard/SetupChecklistWidget";

export default function Dashboard() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/dashboard").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    if (loading) return <PageHeader title="Dashboard"><div className="text-[var(--jr-text-2)]">{t("dashboard_page.loading")}</div></PageHeader>;
    if (!data) return <PageHeader title="Dashboard"><div className="text-[var(--jr-error)]">{t("dashboard_page.load_error")}</div></PageHeader>;

    const k = data.kpis;
    const talentStagesCount = Object.values(data.candidates_by_stage || {}).reduce((a, b) => a + b, 0);
    const widgets = [
        { label: t("dashboard_page.kpi_sales"), value: k.leads, sub: t("dashboard_page.kpi_sales_sub", { n: k.signals }), icon: Briefcase, color: "var(--jr-brand)", testid: "kpi-leads", to: "/app/sales/pipeline" },
        { label: t("dashboard_page.kpi_talent"), value: k.candidates, sub: t("dashboard_page.kpi_talent_sub", { n: talentStagesCount }), icon: Users, color: "var(--jr-amber)", testid: "kpi-candidates", to: "/app/recruit/shortlists" },
        { label: t("dashboard_page.kpi_matches"), value: k.matches, sub: t("dashboard_page.kpi_matches_sub", { n: k.approved_matches }), icon: GitMerge, color: "var(--jr-brand)", testid: "kpi-matches", to: "/app/matches" },
        { label: t("dashboard_page.kpi_response"), value: `${k.response_rate_pct}%`, sub: t("dashboard_page.kpi_response_sub", { n: k.meetings_booked }), icon: TrendingUp, color: "var(--jr-success)", testid: "kpi-response", to: "/app/sales/outreach" },
        { label: t("dashboard_page.kpi_credits"), value: k.credits_remaining, sub: t("dashboard_page.kpi_credits_sub", { n: k.credits_used }), icon: Coins, color: "var(--jr-amber)", testid: "kpi-credits", to: "/app/workspace" },
        { label: t("dashboard_page.kpi_compliance"), value: `${k.compliance_health}%`, sub: t("dashboard_page.kpi_compliance_sub"), icon: ShieldCheck, color: "var(--jr-success)", testid: "kpi-compliance", to: "/app/agents" },
        { label: t("dashboard_page.kpi_campaigns"), value: k.active_campaigns, sub: t("dashboard_page.kpi_campaigns_sub", { n: k.autonomous_runs }), icon: Megaphone, color: "var(--jr-brand)", testid: "kpi-campaigns" },
        { label: t("dashboard_page.kpi_signals"), value: k.signals, sub: t("dashboard_page.kpi_signals_sub"), icon: Radar, color: "var(--jr-brand)", testid: "kpi-signals", to: "/app/sales/signals" },
    ];

    return (
        <PageHeader
            title={t("dashboard_page.greeting", { name: user?.name?.split(" ")[0] || "operator" })}
            subtitle={t("dashboard_page.subtitle")}
        >
            <SetupChecklistWidget />

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {widgets.map((w) => (
                    <KPIWidget key={w.label} {...w} />
                ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-4 mt-4">
                {/* CTA card to chat */}
                <button type="button" onClick={() => openChat({ masterId: "master_sales_agent" })} data-testid="quick-chat-sales" className="text-left rounded-xl border border-[var(--jr-brand)]/40 bg-gradient-to-br from-[var(--jr-brand)]/10 to-transparent p-6 hover:-translate-y-[1px] transition-transform">
                    <div className="flex items-center justify-between">
                        <Sparkles className="text-[var(--jr-brand)]"/>
                        <ArrowUpRight className="text-[var(--jr-text-2)]" size={18}/>
                    </div>
                    <h3 className="font-display text-xl mt-4 tracking-tight">{t("dashboard_page.quick_sales_title")}</h3>
                    <p className="text-sm text-[var(--jr-text-2)] mt-2">{t("dashboard_page.quick_sales_sample")}</p>
                </button>
                <button type="button" onClick={() => openChat({ masterId: "master_recruiter_agent" })} data-testid="quick-chat-recruiter" className="text-left rounded-xl border border-[var(--jr-amber)]/30 bg-gradient-to-br from-[var(--jr-amber)]/10 to-transparent p-6 hover:-translate-y-[1px] transition-transform">
                    <div className="flex items-center justify-between">
                        <MessageSquare className="text-[var(--jr-amber)]"/>
                        <ArrowUpRight className="text-[var(--jr-text-2)]" size={18}/>
                    </div>
                    <h3 className="font-display text-xl mt-4 tracking-tight">{t("dashboard_page.quick_recruit_title")}</h3>
                    <p className="text-sm text-[var(--jr-text-2)] mt-2">{t("dashboard_page.quick_recruit_sample")}</p>
                </button>
                <Link to="/app/matches" data-testid="quick-matches" className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6 hover:-translate-y-[1px] transition-transform">
                    <div className="flex items-center justify-between">
                        <GitMerge className="text-[var(--jr-brand)]"/>
                        <ArrowUpRight className="text-[var(--jr-text-2)]" size={18}/>
                    </div>
                    <h3 className="font-display text-xl mt-4 tracking-tight">{t("dashboard_page.quick_matches_title")}</h3>
                    <p className="text-sm text-[var(--jr-text-2)] mt-2">{t("dashboard_page.quick_matches_sub", { n: k.matches })}</p>
                </Link>
            </div>

            <div className="mt-4 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6">
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="font-display text-lg tracking-tight">{t("dashboard_page.recent_activity")}</h3>
                        <Link to="/app/audit" className="text-xs text-[var(--jr-text-2)] hover:text-white font-mono uppercase tracking-[0.18em]">{t("dashboard_page.audit_log")}</Link>
                    </div>
                    <ul className="space-y-3">
                        {(data.recent_runs || []).slice(0, 8).map((r) => (
                            <li key={r.id}>
                                <Link to="/app/audit" data-testid={`activity-${r.id}`}
                                    className="flex items-start gap-3 border-l-2 border-white/10 pl-3 rounded-r-md py-0.5 hover:bg-white/[0.04] hover:border-[var(--jr-brand)]/50 transition-colors">
                                    <div className="font-mono text-[10px] text-[var(--jr-text-3)] mt-1 uppercase tracking-[0.16em] min-w-[80px]">
                                        {r.agent_kind}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-sm text-white">{r.agent_id}</div>
                                        <div className="text-xs text-[var(--jr-text-2)]">{r.summary}</div>
                                    </div>
                                    <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{(r.cost_credits || 0)}c</span>
                                </Link>
                            </li>
                        ))}
                        {(!data.recent_runs || !data.recent_runs.length) && (
                            <li className="text-sm text-[var(--jr-text-3)]">{t("dashboard_page.no_activity")}</li>
                        )}
                    </ul>
                </div>

                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-6">
                    <h3 className="font-display text-lg tracking-tight mb-5">{t("dashboard_page.pipeline_funnel")}</h3>
                    <Funnel data={data.leads_by_stage} title={t("dashboard_page.sales_stages")} stages={["new", "contacted", "engaged", "meeting", "won"]}
                        linkBase="/app/sales/pipeline" testid="funnel-sales" />
                    <div className="mt-6">
                        <Funnel data={data.candidates_by_stage} title={t("dashboard_page.talent_stages")} stages={["sourced", "contacted", "interested", "screening", "submitted", "placed"]}
                            linkBase="/app/recruit/pipeline" testid="funnel-talent" />
                    </div>
                </div>
            </div>
        </PageHeader>
    );
}

export function PageHeader({ title, subtitle, actions, children }) {
    return (
        <div className="px-8 py-8 max-w-7xl mx-auto">
            <div className="flex items-end justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display text-4xl tracking-tight font-semibold leading-tight">{title}</h1>
                    {subtitle && <p className="mt-1 text-[var(--jr-text-2)]">{subtitle}</p>}
                </div>
                {actions}
            </div>
            {children}
        </div>
    );
}

function KPIWidget({ label, value, sub, icon: Icon, color, testid, to }) {
    const content = (
        <>
            <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{label}</span>
                <Icon size={16} style={{ color }} />
            </div>
            <div className="mt-3 font-display text-4xl tracking-tighter">{value}</div>
            <div className="mt-1 text-xs text-[var(--jr-text-2)] flex items-center justify-between">
                <span>{sub}</span>
                {to && <ArrowUpRight size={12} className="text-[var(--jr-text-3)] opacity-0 group-hover:opacity-100 transition-opacity" />}
            </div>
        </>
    );
    if (to) {
        return (
            <Link to={to} data-testid={testid}
                className="group rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5 hover:-translate-y-[1px] hover:border-white/20 transition-all">
                {content}
            </Link>
        );
    }
    return (
        <div data-testid={testid} className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5">
            {content}
        </div>
    );
}

function Funnel({ data, title, stages, linkBase, testid }) {
    const total = stages.reduce((a, s) => a + (data?.[s] || 0), 0) || 1;
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-2 font-mono">{title}</div>
            <div className="space-y-1.5">
                {stages.map((s) => {
                    const n = data?.[s] || 0;
                    const pct = Math.max(4, Math.round((n / total) * 100));
                    const row = (
                        <>
                            <span className="w-20 text-xs text-[var(--jr-text-2)] capitalize">{s}</span>
                            <div className="flex-1 h-2 rounded bg-white/5 overflow-hidden">
                                <div className="h-full bg-[var(--jr-brand)]" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-6 text-right text-xs text-white font-mono">{n}</span>
                        </>
                    );
                    if (!linkBase) return <div key={s} className="flex items-center gap-3">{row}</div>;
                    return (
                        <Link key={s} to={`${linkBase}?stage=${s}`} data-testid={`${testid}-${s}`}
                            className="flex items-center gap-3 rounded-md px-1 -mx-1 py-0.5 hover:bg-white/[0.05] transition-colors group/frow">
                            {row}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
